import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useI18n } from "../../i18n/LanguageProvider";
import { Container } from "../Container";
import { MaskedLines } from "../Reveal";
import { AppStoreButton } from "../AppButtons";
import { StoreBadges } from "../StoreBadges";
import { CollectionsPanel, MoneyPill } from "../AppUI";
import { withStripe } from "../StripeMark";

const ease = [0.22, 1, 0.36, 1];

export function Hero() {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPanel = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const yPhoto = useTransform(scrollYProgress, [0, 1], [0, -110]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-[#EAF7F0] pt-32 md:pt-40">
      {/* soft radial glow */}
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(70% 60% at 75% 25%, rgba(77,215,154,0.35) 0%, rgba(8,172,125,0.08) 45%, transparent 75%)" }} />

      <Container className="relative">
        <div className="grid grid-cols-1 items-center gap-16 pb-20 md:pb-28 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.05 }}
              className="mb-8 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/70 px-4 py-1.5 text-xs font-medium text-black/60 backdrop-blur"
            >
              <span className="h-1.5 w-1.5 rounded-full brand-gradient" />
              {withStripe(t("hero.note"))}
            </motion.p>

            <h1 className="text-5xl font-semibold leading-[1.04] tracking-tighter text-black sm:text-6xl lg:text-[4.5rem]">
              <MaskedLines lines={[t("hero.line1")]} />
              <span className="block overflow-hidden pb-[0.18em]">
                <motion.span
                  className="block brand-text"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1, ease, delay: 0.22 }}
                >
                  {t("hero.line2")}
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.45 }}
              className="mt-7 max-w-xl text-lg leading-relaxed text-black/60 md:text-xl"
            >
              {t("hero.subtitle")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.6 }}
              className="mt-9 flex flex-col items-start gap-3 sm:flex-row sm:items-center"
            >
              <AppStoreButton label={t("hero.ctaPrimary")} testId="hero-cta-primary" className="px-9 py-4 text-base" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, ease, delay: 0.78 }}
              className="mt-8"
            >
              <p className="mb-3 text-xs font-medium uppercase tracking-[0.15em] text-black/40">{t("hero.availability")}</p>
              <StoreBadges height={48} />
            </motion.div>
          </div>

          {/* Right — UI recreation + floating person + money pill */}
          <div className="relative mx-auto w-full max-w-[440px] lg:mx-0 lg:ml-auto">
            <motion.div style={{ y: yPanel }} className="relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 40, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1, ease, delay: 0.35 }}
              >
                <CollectionsPanel className="max-w-[360px]" />
              </motion.div>
            </motion.div>

            {/* floating person photo */}
            <motion.div
              style={{ y: yPhoto }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease, delay: 0.5 }}
              className="absolute -right-2 -top-6 z-0 h-36 w-28 overflow-hidden rounded-[1.4rem] border-4 border-white shadow-[0_24px_50px_-20px_rgba(0,0,0,0.4)] sm:h-44 sm:w-36 lg:-right-6"
            >
              <img src="/people/p2.jpg" alt={`Centauros — ${t("hero.line1")} ${t("hero.line2")}`} loading="lazy" decoding="async" width="288" height="352" className="h-full w-full object-cover" draggable="false" />
            </motion.div>

            {/* floating money pill */}
            <MoneyPill
              label={t("appui.collected")}
              amount="420,00 €"
              delay={0.7}
              className="absolute -bottom-5 left-0 z-20 sm:-left-6"
            />
          </div>
        </div>
      </Container>
    </section>
  );
}
